import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import getAccessToken from 'hooks/getAccessToken'
import useTeamID from 'hooks/useTeamID'
import { listSharesByConsumer, sharingEnabled } from '../../Events/SendToPatient/api'

const PAGE = 20

const STATUS_LABEL_KEY = {
  sent: ['share_status_sent', 'Enviado ✓'],
  notify_failed: ['share_status_notify_failed', 'Guardado — falta notificar'],
  recording: ['share_status_recording', 'Guardado parcial — reintentar'],
  conflict: ['share_status_conflict', 'No se pudo notificar'],
  failed: ['share_status_failed', 'Falló — rehacer el envío'],
}

// V2 (card "Envío de archivos a pacientes de Crontu"): historial de
// archivos enviados a ESTE paciente, cruzando todos sus turnos — mismo
// patrón visual que el historial de turnos de Form.js (card blanca,
// paginado client-side de a 20), pero con fetch manual: el servicio de
// sharing es un origin distinto (mismo criterio que
// SendToPatientModal.js#refreshHistory, no el `hooks/useQuery` del api
// principal).
// `refreshKey`: hallazgo real de review — un envío exitoso hecho DESDE la
// ficha (SendFilesButton) no refrescaba esta sección (fetch único por
// consumerID, sin ninguna forma de que el modal le avisara al padre).
// Form.js lo bump-ea en el onSent de SendFilesButton.
const ConsumerSharesHistory = ({ consumerID, refreshKey }) => {
  const { t } = useTranslation()
  const teamID = useTeamID()
  const token = getAccessToken()
  const [shares, setShares] = useState(null)
  const [error, setError] = useState(false)
  const [visible, setVisible] = useState(PAGE)

  useEffect(() => {
    if (!token.value || !teamID || !consumerID) return
    let cancelled = false
    setVisible(PAGE)
    listSharesByConsumer({ token: token.value, teamID, consumerID })
      .then(rows => {
        if (cancelled) return
        setShares(rows)
        setError(false)
      })
      .catch(() => {
        if (cancelled) return
        setShares([])
        setError(true)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token.value, teamID, consumerID, refreshKey])

  if (!sharingEnabled()) return null

  return (
    <div className="bg-white shadow px-4 py-3 rounded mt-4" data-testid="consumer-shares-history">
      <h4 className="h6 mb-2">{t('shares_history', 'Archivos enviados')}</h4>
      {error && (
        <p className="text-danger small mb-0" data-testid="consumer-shares-error">
          {t('shares_history_error', 'No pudimos cargar el historial de archivos enviados.')}
        </p>
      )}
      {!error && shares?.length === 0 && (
        <p className="text-secondary small mb-0">{t('shares_history_empty', 'Sin archivos enviados a este paciente.')}</p>
      )}
      {!error && shares?.length > 0 && (
        <ul className="list-unstyled m-0">
          {shares.slice(0, visible).map(share => {
            const labelKey = STATUS_LABEL_KEY[share.status]
            const fileNames = (share.files || []).map(f => f.title || f.filename).join(', ')
            return (
              <li key={share.id} className="border-top pt-2 mt-2">
                <div className="d-flex justify-content-between flex-wrap gap-2">
                  <span className="text-secondary small">{new Date(share.createdAt).toLocaleString()}</span>
                  <span className="badge bg-light text-dark border">{labelKey ? t(...labelKey) : share.status}</span>
                </div>
                {share.professional?.name && <div className="small text-secondary">{share.professional.name}</div>}
                {fileNames && <div className="small">{fileNames}</div>}
              </li>
            )
          })}
        </ul>
      )}
      {!error && shares?.length > visible && (
        <button type="button" className="btn btn-link p-0 mt-2" onClick={() => setVisible(v => v + PAGE)}>
          {t('see_more', 'Ver más')}
        </button>
      )}
    </div>
  )
}

export default ConsumerSharesHistory
